import { CLASS_NAMES, EVENTS, SELECTORS } from '@bs/constants';
import { MessageBus } from '@bs/utils';

/**
 * A class for the Pie Chart module.
 */
export default class StatsFwPie extends MessageBus {
  /**
   * Constructor for StatsFwPie.
   * @param {HTMLElement} element - The root element of the pie chart module.
   */
  constructor(element) {
    super();
    this.element = element;
    this.resizeTimeout = null;
    this.init();
  }

  /**
   * Initializes the module by setting up DOM references and event handlers.
   * @returns {StatsFwPie} The current instance for chaining.
   */
  init() {
    this.cacheDomReferences().setupHandlers().enable();
    return this;
  }

  /**
   * Cache DOM References.
   * Stores necessary DOM elements for animation.
   * @returns {StatsFwPie} The current instance for chaining.
   */
  cacheDomReferences() {
    this.piePercentage = this.element.querySelectorAll(
      `.${CLASS_NAMES.LARGE_PIE}[${SELECTORS.DATA_TARGET_PCT}]`
    );
    this.fullCircles = this.element.querySelectorAll(
      `.${CLASS_NAMES.FW_STAT_STAT_MD_PIE}:not(.${CLASS_NAMES.LARGE_PIE}) ${SELECTORS.SVG} ${SELECTORS.CIRCLE}`
    );

    return this;
  }

  /**
   * Set up event handlers for interactions.
   * @returns {StatsFwPie} The current instance for chaining.
   */
  setupHandlers() {
    this.onResizeHandler = this.onResize.bind(this);
    this.onCounterStartHandler = this.onCounterStart.bind(this);
    return this;
  }

  /**
   * Enables event listeners and triggers animations.
   * @returns {StatsFwPie} The current instance for chaining.
   */
  enable() {
    if (this.piePercentage.length === 0 && this.fullCircles.length === 0) {
      return this;
    }

    this.element.addEventListener(
      'fwStatCounter:start',
      this.onCounterStartHandler
    );
    window.addEventListener(EVENTS.RESIZE, this.onResizeHandler);

    return this;
  }

  onCounterStart() {
    this.piePercentage.forEach((pie) => this.animateArcWithPercentage(pie));
    this.fullCircles.forEach((circle) => this.animateFullCircle(circle));
  }

  /**
   * Animates the arc for percentage-based pies.
   * @param {HTMLElement} element - The pie chart element to animate.
   */
  animateArcWithPercentage(element) {
    const targetPct = parseInt(
      element.getAttribute(`${SELECTORS.DATA_TARGET_PCT}`),
      10
    );
    if (Number.isNaN(targetPct)) return;

    const totalDuration = 1000;
    const fgDuration = totalDuration * (targetPct / 100);
    const bgDuration = totalDuration - fgDuration;
    const startTime = performance.now();

    const arcs = element.querySelectorAll(
      `${SELECTORS.SVG} ${SELECTORS.CIRCLE}`
    );

    const backgroundArc = arcs[0];
    const foregroundArc = arcs[1];

    const circumference = 1570.796;

    backgroundArc.setAttribute(
      `${SELECTORS.STROKE_DASHARRAY}`,
      `0 ${circumference}`
    );
    foregroundArc.setAttribute(
      `${SELECTORS.STROKE_DASHARRAY}`,
      `0 ${circumference}`
    );

    const animateForeground = (currentTime) => {
      const elapsedTime = currentTime - startTime;
      const progress = Math.min(elapsedTime / fgDuration, 1);
      const fgDashLength = (circumference / 100) * targetPct * progress;

      foregroundArc.setAttribute(
        `${SELECTORS.STROKE_DASHARRAY}`,
        `${fgDashLength} ${circumference - fgDashLength}`
      );

      if (progress < 1) {
        requestAnimationFrame(animateForeground);
      } else {
        this.animateBackground(
          backgroundArc,
          fgDashLength,
          circumference,
          bgDuration
        );
      }
    };

    requestAnimationFrame(animateForeground);
  }

  /**
   * Animates a full-circle (non-percentage based pie).
   * @param {SVGElement} circle - The SVG circle element to animate.
   * @returns {StatsFwPie} The current instance for chaining.
   */
  animateFullCircle(circle) {
    const circumference = 1570.796;
    const totalDuration = 1000;
    const startTime = performance.now();

    const animateCircle = (currentTime) => {
      const elapsedTime = currentTime - startTime;
      const progress = Math.min(elapsedTime / totalDuration, 1);
      const dashLength = progress * circumference;

      circle.setAttribute(
        `${SELECTORS.STROKE_DASHARRAY}`,
        `${dashLength} ${circumference - dashLength}`
      );

      if (progress < 1) {
        requestAnimationFrame(animateCircle);
      }
    };

    requestAnimationFrame(animateCircle);
    return this;
  }

  /**
   * Animates the background arc after the foreground reaches its target.
   * @param {SVGElement} arc - The SVG background arc element.
   * @param {number} fgDashLength - The length of the foreground arc dash.
   * @param {number} circumference - The total circumference of the arc.
   * @param {number} duration - The duration of the animation in milliseconds.
   * @returns {StatsFwPie} The current instance for chaining.
   */
  animateBackground(arc, fgDashLength, circumference, duration) {
    const startTime = performance.now();

    const updateBackground = (currentTime) => {
      const elapsedTime = currentTime - startTime;
      const progress = Math.min(elapsedTime / duration, 1);
      const bgDashLength =
        fgDashLength + progress * (circumference - fgDashLength);

      arc.setAttribute(
        `${SELECTORS.STROKE_DASHARRAY}`,
        `${bgDashLength} ${circumference - bgDashLength}`
      );

      if (progress < 1) {
        requestAnimationFrame(updateBackground);
      }
    };

    requestAnimationFrame(updateBackground);
    return this;
  }

  /**
   * Handles window resize events and restarts animations.
   */
  onResize() {
    clearTimeout(this.resizeTimeout);
    this.resizeTimeout = setTimeout(() => {
      this.restartAnimations();
    }, 300);
  }

  /**
   * Restarts animations for all pies after a resize.
   */
  restartAnimations() {
    this.piePercentage.forEach((pie) => {
      this.animateArcWithPercentage(pie);
    });

    this.fullCircles.forEach((circle) => {
      this.animateFullCircle(circle);
    });
  }
}
